import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { payrollPayments, PAYOUT_KIND_LABELS, type PayoutKind } from '@/api/payrolls';
import {
  taxRemittances,
  TAX_TYPE_LABELS,
  type TaxType,
  type TaxRemittanceItemIn,
} from '@/api/tax_remittances';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import OrgTaxAccountPicker, { type OrgTaxItem } from '@/components/OrgTaxAccountPicker';
import { renderTaxPurposeTemplate } from '@/shared/taxPurposeTemplate';
import { orgOwnCode } from '@/shared/utils/partyCodes';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const now = new Date();
const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Дефолтний рахунок обліку за видом податку.
const DEFAULT_ACC: Record<TaxType, string> = { PDFO: '6411', WZ: '642', ESV: '651' };

interface ItemState extends TaxRemittanceItemIn {}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  bank_account_id: string;
  comment: string;
  items: ItemState[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: now.toISOString().slice(0, 16),
  period_year: now.getFullYear(),
  period_month: now.getMonth() + 1,
  bank_account_id: '',
  comment: '',
  items: [],
};

export default function TaxRemittanceEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromPaymentId = searchParams.get('from_payment');
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');

  const docQuery = useQuery({
    queryKey: ['tax-remittance', id],
    queryFn: () => taxRemittances.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'tax-remittance', (cid) => taxRemittances.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);
  const [fromLoaded, setFromLoaded] = useState(false);
  // Податки з виплати пропорційні фактично виплаченому (часткова виплата).
  const [fromScaled, setFromScaled] = useState(false);
  // Суми зменшено залишком боргу бюджету (частину податків уже перераховано).
  const [fromCapped, setFromCapped] = useState(false);

  const bankQ = useQuery({
    queryKey: ['bank-accounts', form.organization_id],
    queryFn: () => refs.bankAccounts(form.organization_id),
    enabled: !!form.organization_id,
  });

  // «На основі виплати»: ПРОВЕДЕНІ виплати нарахувань обраного періоду —
  // вибір документа заповнює рядки податків (пропорція + cap, як кнопка з відомості).
  const [basePaymentId, setBasePaymentId] = useState('');
  // Виплата, з якої прийшли (?from_payment=) — щоб показати її обраною у select,
  // навіть якщо її немає у списку (ще не проведена / список не завантажився).
  const [basePaymentDoc, setBasePaymentDoc] =
    useState<{ id: string; document_number: string; payout_kind?: string; total_amount: string } | null>(null);
  const isPostedDoc = !isNew && (docQuery.data?.posted ?? false);
  const paymentsQ = useQuery({
    queryKey: ['posted-payments', form.organization_id, form.period_year, form.period_month],
    queryFn: () => payrollPayments.list({
      organization_id: form.organization_id,
      period_year: form.period_year, period_month: form.period_month,
      posted: true, limit: 200,
    }),
    enabled: !!form.organization_id && !isPostedDoc,
  });
  // Опції select'а: список проведених + (якщо нема у списку) виплата-джерело.
  const paymentOptions = useMemo(() => {
    const items = paymentsQ.data?.items ?? [];
    if (basePaymentDoc && !items.some((p) => p.id === basePaymentDoc.id)) {
      return [basePaymentDoc, ...items];
    }
    return items;
  }, [paymentsQ.data, basePaymentDoc]);

  // Заповнити рядки податків з виплати (спільне для select'а і ?from_payment=).
  async function applyFromPayment(paymentId: string, orgId: string) {
    try {
      const r = await taxRemittances.fromPayment({
        organization_id: orgId, payroll_payment_id: paymentId,
      });
      setForm((f) => ({
        ...f,
        organization_id: r.organization_id,
        period_year: r.period_year,
        period_month: r.period_month,
        bank_account_id: r.bank_account_id ?? f.bank_account_id,
        items: r.rows.map((row) => ({
          tax_type: row.tax_type, account_code: row.account_code, amount: row.amount,
          recipient_name: row.recipient_name, recipient_edrpou: row.recipient_edrpou,
          recipient_iban: row.recipient_iban, payment_purpose: row.payment_purpose,
          activity_place_id: row.activity_place_id ?? null,
        })),
      }));
      setFromScaled(r.scaled ?? false);
      setFromCapped(r.capped ?? false);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Не вдалося підтягнути податки з виплати');
    }
  }

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setBasePaymentId(d.base_payment_id ?? '');
      // Підпис виплати-джерела для select'а (може бути іншого періоду/не проведена).
      if (d.base_payment_id) {
        payrollPayments.get(d.base_payment_id).then((pay) => setBasePaymentDoc({
          id: pay.id, document_number: pay.document_number,
          payout_kind: pay.payout_kind, total_amount: pay.total_amount,
        })).catch(() => setBasePaymentDoc(null));
      }
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        period_year: d.period_year,
        period_month: d.period_month,
        bank_account_id: d.bank_account_id ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          tax_type: it.tax_type, account_code: it.account_code, amount: it.amount,
          recipient_name: it.recipient_name, recipient_edrpou: it.recipient_edrpou,
          recipient_iban: it.recipient_iban, payroll_id: it.payroll_id,
          payment_purpose: it.payment_purpose,
          activity_place_id: it.activity_place_id ?? null,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  // Заповнення з виплати нарахування (?from_payment=): один раз при відкритті.
  useEffect(() => {
    if (!isNew || !fromPaymentId || fromLoaded) return;
    setFromLoaded(true);
    (async () => {
      // org нам ще не відома до відповіді — endpoint вимагає org:
      // тягнемо org з виплати через payrollPayments.get.
      try {
        const pay = await payrollPayments.get(fromPaymentId);
        setBasePaymentId(fromPaymentId);
        setBasePaymentDoc({
          id: pay.id, document_number: pay.document_number,
          payout_kind: pay.payout_kind, total_amount: pay.total_amount,
        });
        await applyFromPayment(fromPaymentId, pay.organization_id);
      } catch (e: any) {
        setError(e?.response?.data?.detail ?? 'Не вдалося підтягнути податки з виплати');
      }
    })();
  }, [isNew, fromPaymentId, fromLoaded]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const paymentDocs = docQuery.data?.payment_docs ?? [];
  const makeDocs = useMutation({
    mutationFn: () => taxRemittances.createBankPayments(id!),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['tax-remittance', id] }); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Не вдалося створити платіжні інструкції'),
  });

  const total = useMemo(
    () => form.items.reduce((s, it) => s + Number(it.amount || 0), 0),
    [form.items],
  );

  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, { tax_type: 'PDFO', account_code: DEFAULT_ACC.PDFO, amount: '0' }],
    }));
  }
  // ч.767: «створити рахунок» → рахунок з картки організації (або новий, що
  // зберігається у картку) → рядок з отримувачем і призначенням за шаблоном.
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const currentOrg = orgList.data?.items.find((o) => o.id === form.organization_id);
  function addRowFromAccount(it: OrgTaxItem) {
    const taxType = (it.tax_type ?? 'PDFO') as TaxType;
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        tax_type: taxType, account_code: DEFAULT_ACC[taxType], amount: '0',
        recipient_name: it.recipient_name, recipient_edrpou: it.recipient_edrpou,
        recipient_iban: it.recipient_iban,
        activity_place_id: it.activity_place_id ?? null,
        payment_purpose: renderTaxPurposeTemplate(it.purpose_template, {
          tax_type: taxType, tax_label: it.name, edrpou: orgOwnCode(currentOrg),
          year: Number(f.period_year), month: Number(f.period_month), org: currentOrg?.name ?? null,
        }),
      }],
    }));
  }
  function updRow(i: number, patch: Partial<ItemState>) {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], ...patch } as ItemState;
      return { ...f, items };
    });
  }
  function removeRow(i: number) {
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  }

  // Update-схема backend'а — extra=forbid БЕЗ organization_id (орг не змінюється
  // після створення) → на PATCH організацію НЕ шлемо, інакше 422.
  function payload() {
    return {
      document_number: form.document_number || undefined,
      document_date: form.document_date,
      period_year: form.period_year,
      period_month: form.period_month,
      bank_account_id: form.bank_account_id || null,
      base_payment_id: basePaymentId || null,
      comment: form.comment || null,
      items: form.items.filter((it) => Number(it.amount) > 0),
    };
  }
  const createPayload = () => ({ organization_id: form.organization_id, ...payload() });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return taxRemittances.create(createPayload());
      return taxRemittances.update(id!, payload());
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tax-remittances'] });
      if (isNew) { qc.setQueryData(['tax-remittance', doc.id], doc); navigate(`/tax-remittances/${doc.id}`); }
      else qc.invalidateQueries({ queryKey: ['tax-remittance', id] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });

  const saveAndPost = useMutation({
    mutationFn: async () => {
      const doc = isNew
        ? await taxRemittances.create(createPayload())
        : await taxRemittances.update(id!, payload());
      try {
        await taxRemittances.post(doc.id);
      } catch (e) {
        // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
        if (isNew) navigate(`/tax-remittances/${doc.id}`, { replace: true });
        throw e;
      }
      return taxRemittances.get(doc.id);
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tax-remittances'] });
      qc.setQueryData(['tax-remittance', doc.id], doc);
      navigate(`/tax-remittances/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });

  // ч.779: після розпроведення — скільки чернеток платіжок видалено (там, де
  // до цього писали про створені).
  const [deletedDocs, setDeletedDocs] = useState<number>(0);
  const unpost = useMutation({
    mutationFn: async () => {
      const had = paymentDocs.length;
      const fresh = await taxRemittances.unpost(id!);
      qc.setQueryData(['tax-remittance', id], fresh);
      setDeletedDocs(had);
      return fresh;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tax-remittances'] }),
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });

  const remove = useMutation({
    mutationFn: () => taxRemittances.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-remittances'] });
      navigate('/tax-remittances');
    },
  });

  const canSave = !!form.organization_id && !!form.document_date && !isPosted;

  // ч.878 (новий дизайн, Закон #10): «ще дії» (ч.765: створити платіжні
  // інструкції) → «Повʼязані дії» правої колонки — дослівно.
  const railActions = [{
    // ч.1347: платіжки — зі збереженого документа, проведення не чекаємо.
    label: 'створити платіжні інструкції',
    disabled: isNew || makeDocs.isPending,
    title: isNew ? 'Спершу збережіть документ'
      : 'Платіжка-чернетка на кожен податок → Банк → Списання → збережені',
    onClick: () => makeDocs.mutate(),
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'base', title: 'Період і підстава', required: 0, filled: 0 },
    { id: 'items', title: 'Податки', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове перерахування податків' : `Перерахування ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          isNew={isNew}
          onBackToList={() => navigate('/tax-remittances')}
          onSave={() => save.mutate()}
          saveDisabled={!canSave}
          savePending={save.isPending || saveAndPost.isPending}
          isPosted={isPosted}
          onPost={() => saveAndPost.mutate()}
          onUnpost={() => unpost.mutate()}
          postPending={saveAndPost.isPending}
          unpostPending={unpost.isPending}
          deletable={!isNew}
          onDelete={() => { if (confirm('Видалити документ?')) remove.mutate(); }}
          deletePending={remove.isPending}
          entriesDoc={{ type: 'doc.tax_remittances', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {isPosted && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>
          {paymentDocs.length === 0
            ? 'Платіжних інструкцій не створено — «Повʼязані дії → створити платіжні інструкції».'
            : (<>Створено платіжних інструкцій: {paymentDocs.length}{' '}
                <Link to="/bank-payments?tab=saved">дивитись</Link></>)}
        </div>
      )}
      {!isPosted && deletedDocs > 0 && (
        <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', border: '1px solid var(--badge-amber-bd)',
          padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
          Розпроведено. Видалено платіжних інструкцій: {deletedDocs} (чернетки, створені
          після проведення цього документа). Якщо зарплатна платіжка посилалась на них —
          на ній знову «!», перепривʼяжіть після повторного створення.
        </div>
      )}

      {error && (
        <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}
      {fromScaled && (
        <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', border: '1px solid var(--badge-amber-bd)',
          padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
          ⚠ Виплата часткова — податки розраховано пропорційно фактично виплаченим
          сумам (не з усього нарахування). Суми можна відредагувати — наприклад,
          сплатити податки повністю; наступна виплата це врахує.
        </div>
      )}
      {fromCapped && (
        <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', border: '1px solid var(--badge-amber-bd)',
          padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
          ⚠ Частину податків за цей період уже перераховано раніше — суми зменшено
          до неперерахованого залишку боргу бюджету.
        </div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/tax-remittances/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.tax_remittances', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew}
            onChange={(e) => { setBasePaymentId(''); setBasePaymentDoc(null); setForm({ ...form, organization_id: e.target.value }); }}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover value={form.document_date.slice(0, 10)}
            onChange={(v) => setForm({ ...form, document_date: `${v}T00:00` })}
            disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Період і підстава">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={form.period_month} disabled={isPosted}
              onChange={(e) => { setBasePaymentId(''); setBasePaymentDoc(null); setForm({ ...form, period_month: Number(e.target.value) }); }}
              style={{ flex: 2 }}>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={form.period_year} disabled={isPosted}
              onChange={(e) => { setBasePaymentId(''); setBasePaymentDoc(null); setForm({ ...form, period_year: Number(e.target.value) }); }}
              style={{ flex: 1 }}>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>На основі виплати (проведені за період)</label>
          <select value={basePaymentId}
              onChange={(e) => {
                const pid = e.target.value;
                setBasePaymentId(pid);
                if (pid) applyFromPayment(pid, form.organization_id);
              }}
              disabled={!form.organization_id || isPosted}>
              <option value="">
                {!form.organization_id ? '— спершу оберіть організацію —'
                  : paymentsQ.isFetching ? '— завантаження…'
                  : paymentOptions.length === 0
                    ? '— немає проведених виплат за період —'
                    : '— оберіть виплату —'}
              </option>
              {paymentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.document_number}
                  {' — '}{PAYOUT_KIND_LABELS[(p.payout_kind ?? 'MAIN') as PayoutKind] ?? p.payout_kind}
                  {' — '}{fmt2(p.total_amount)} грн
                </option>
              ))}
            </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Р/р організації (звідки переказ)</label>
          <select value={form.bank_account_id} disabled={isPosted}
            onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}>
            <option value="">— головний рахунок —</option>
            {bankQ.data?.map((b) => (
              <option key={b.id} value={b.id}>{b.is_main ? '★ ' : ''}{b.account_name || b.iban}</option>
            ))}
          </select>
        </div>
      </div>

      </DocBlock>

      <DocBlock index={3} title="Податки"
        actions={!isPosted ? (<>
          <button type="button" className="btn-doc" disabled={!currentOrg}
            title="Рахунок для сплати податку з картки організації (або створити новий — збережеться у картку)"
            onClick={() => setAccountPickerOpen(true)}>створити рахунок</button>
          <button type="button" className="primary" onClick={addRow}>+ додати рядок</button>
        </>) : undefined}>
      {/* Рядки податків */}
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 130 }}>Податок</th>
            <th style={{ width: 70 }}>Рахунок</th>
            <th style={{ width: 120, textAlign: 'right' }}>Сума</th>
            <th style={{ width: 150 }}>Отримувач</th>
            <th style={{ width: 200 }}>IBAN отримувача</th>
            <th>Призначення платежу</th>
            <th style={{ width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>
                {isPosted ? TAX_TYPE_LABELS[it.tax_type] : (
                  <select value={it.tax_type}
                    onChange={(e) => updRow(i, {
                      tax_type: e.target.value as TaxType,
                      account_code: DEFAULT_ACC[e.target.value as TaxType],
                    })} style={{ width: '100%' }}>
                    {(Object.keys(TAX_TYPE_LABELS) as TaxType[]).map((t) => (
                      <option key={t} value={t}>{TAX_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                )}
                {/* ч.806: ПДФО відокремленого підрозділу — назва місця під податком. */}
                {it.activity_place_id && (
                  <div style={{ fontSize: '0.7rem', color: '#2563eb' }}
                    title="ПДФО до місцевого бюджету за місцем відокремленого підрозділу">
                    {currentOrg?.activity_places?.find((p) => p.id === it.activity_place_id)?.name
                      ?? 'відокремлений підрозділ'}
                  </div>
                )}
              </td>
              <td>
                {isPosted ? it.account_code : (
                  <input value={it.account_code} style={{ width: '100%' }}
                    onChange={(e) => updRow(i, { account_code: e.target.value })} />
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {isPosted ? fmt2(it.amount) : (
                  <input type="number" step="0.01" value={it.amount}
                    style={{ width: '100%', textAlign: 'right' }}
                    onChange={(e) => updRow(i, { amount: e.target.value })} />
                )}
              </td>
              <td>
                {isPosted ? (it.recipient_name ?? '—') : (
                  <input value={it.recipient_name ?? ''} placeholder="ДПС…" style={{ width: '100%' }}
                    onChange={(e) => updRow(i, { recipient_name: e.target.value || null })} />
                )}
              </td>
              <td>
                {isPosted ? (it.recipient_iban ?? '—') : (
                  <input value={it.recipient_iban ?? ''} placeholder="UA…" maxLength={34}
                    style={{ width: '100%' }}
                    onChange={(e) => updRow(i, {
                      recipient_iban: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || null,
                    })} />
                )}
              </td>
              <td>
                {isPosted ? (it.payment_purpose ?? '—') : (
                  <input value={it.payment_purpose ?? ''} placeholder="101 …" style={{ width: '100%' }}
                    onChange={(e) => updRow(i, { payment_purpose: e.target.value || null })} />
                )}
              </td>
              <td>
                {!isPosted && (
                  <button className="danger" style={{ padding: '0.2rem 0.5rem' }}
                    onClick={() => removeRow(i)}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} style={{ fontWeight: 600 }}>Разом</td>
            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt2(total)}</td>
            <td colSpan={4} />
          </tr>
        </tfoot>
      </TchTable>

      {!isPosted && accountPickerOpen && currentOrg && (
        <OrgTaxAccountPicker organization={currentOrg}
          onPick={addRowFromAccount} onClose={() => setAccountPickerOpen(false)} />
      )}
      {form.items.length === 0 && (
        <p style={{ color: 'var(--muted)', margin: '0.5rem 0 0' }}>
          Оберіть виплату у полі «На основі виплати» — рядки податків заповняться, або додайте рядки вручну.
        </p>
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.tax_remittances'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
